/**
 * Tâche 1224 (Eric 2026-06-26) — Jeton de session dans l'URL de lecture.
 *
 * La tâche 1210 a remplacé ce jeton par un jeton limité à une vidéo, mais le
 * jeton de session reste toléré pour ne casser aucun lien déjà ouvert. Cette
 * carte répond à la seule question qui permet de trancher : **combien de liens
 * s'en servent encore, et qui ?** Quand le compte tombe à zéro, un bouton le
 * refuse définitivement — réglage persisté et réversible, parce qu'un cas
 * oublié doit pouvoir être débloqué sans déploiement.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, Link as LinkIcon } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";

const API = process.env.REACT_APP_BACKEND_URL;

export const LegacyStreamTokenCard = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/legacy-stream-token?days=30`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const toggle = async (allowed) => {
    const ok = await confirmDialog(
      allowed
        ? "Rétablir le jeton de session dans l'URL de lecture ?\n\n" +
            "• Les anciens liens fonctionneront de nouveau\n" +
            "• Un lien copié redonnera l'accès au compte entier pendant 7 jours"
        : "Refuser définitivement le jeton de session dans l'URL ?\n\n" +
            `• ${data?.uses_total ?? 0} usage(s) enregistré(s) sur les 30 derniers jours\n` +
            "• Les onglets encore ouverts avec un ancien lien afficheront un écran noir jusqu'au rechargement\n" +
            "• Réversible : ce même bouton le rétablit",
      { danger: !allowed },
    );
    if (!ok) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/legacy-stream-token`,
        { allowed },
        { withCredentials: true },
      );
      setData(r.data);
      toast.success(
        allowed
          ? "Jeton de session rétabli dans l'URL de lecture."
          : "Jeton de session refusé. Seul le jeton limité à une vidéo est accepté.",
      );
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const allowed = data?.allowed !== false;
  const uses = data?.uses_total ?? 0;
  const state = loading
    ? "loading"
    : !data
      ? "unknown"
      : !allowed
        ? "cut"
        : uses > 0
          ? "waiting"
          : "ready";
  const box = {
    loading: "bg-zinc-50 text-zinc-700 border-zinc-200",
    unknown: "bg-zinc-100 text-zinc-800 border-zinc-300",
    cut: "bg-emerald-50 text-emerald-900 border-emerald-300",
    ready: "bg-amber-50 text-amber-900 border-amber-300",
    waiting: "bg-amber-50 text-amber-900 border-amber-300",
  }[state];
  const label = {
    loading: "Chargement…",
    unknown: "État inconnu",
    cut: "Jeton de session refusé — seul le jeton limité à une vidéo est accepté",
    ready: "Plus aucun lien n'utilise le jeton de session : vous pouvez le couper",
    waiting: `${uses} lien(s) utilisent encore le jeton de session`,
  }[state];
  const dot = {
    loading: "bg-zinc-300",
    unknown: "bg-zinc-400",
    cut: "bg-emerald-500",
    ready: "bg-amber-500",
    waiting: "bg-amber-500",
  }[state];

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="legacy-stream-token-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <LinkIcon size={18} weight="duotone" /> Jeton de session dans l&apos;URL de lecture
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">
            Le lecteur demande désormais un jeton limité à une seule vidéo (tâche 1210).
            L&apos;ancien jeton de session reste toléré pour ne casser aucun lien déjà
            ouvert — un lien copié donnerait pourtant accès au compte entier pendant
            7 jours. Dès que le compteur tombe à zéro, coupez-le.
          </p>
        </div>
        <div className="flex gap-2">
          {allowed ? (
            <button
              onClick={() => toggle(false)}
              disabled={saving}
              className="px-3 py-2 text-sm rounded-lg bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-50 transition-colors"
              data-testid="legacy-stream-token-cut-btn"
              title={uses > 0
                ? `Attention : ${uses} lien(s) s'en servent encore sur les 30 derniers jours`
                : "Aucun usage enregistré : la coupure ne casse rien"}
            >
              {saving ? "Enregistrement…" : "Refuser le jeton de session"}
            </button>
          ) : (
            <button
              onClick={() => toggle(true)}
              disabled={saving}
              className="px-3 py-2 text-sm rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
              data-testid="legacy-stream-token-restore-btn"
            >
              {saving ? "Enregistrement…" : "Rétablir"}
            </button>
          )}
          <button
            onClick={load}
            className="px-3 py-2 text-sm rounded-lg border border-zinc-300 hover:bg-zinc-50 transition-colors"
            data-testid="legacy-stream-token-refresh-btn"
            aria-label="Rafraîchir"
          >
            <ArrowClockwise size={16} />
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-lg border p-4 ${box}`} data-testid="legacy-stream-token-overall">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`w-3 h-3 rounded-full ${dot}`} aria-hidden />
          <span className="font-semibold" data-testid="legacy-stream-token-verdict">{label}</span>
          <span className="text-xs opacity-80" data-testid="legacy-stream-token-counts">
            {uses} usage(s) sur 30 jours · {data?.uses_last_7_days ?? 0} sur 7 jours
            {data?.last_use_at
              ? ` · dernier le ${new Date(data.last_use_at).toLocaleString("fr-CA", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}`
              : " · jamais"}
          </span>
        </div>
      </div>

      {data?.users?.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="legacy-stream-token-users-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3 font-medium">Compte</th>
                <th className="py-2 pr-3 font-medium">Usages</th>
                <th className="py-2 pr-3 font-medium">Vidéos</th>
                <th className="py-2 font-medium">Dernier usage</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr
                  key={u.email}
                  className="border-b border-zinc-100 last:border-0"
                  data-testid={`legacy-stream-token-user-${u.email}`}
                >
                  <td className="py-2 pr-3 break-words min-w-0">{u.email}</td>
                  <td className="py-2 pr-3 tabular-nums">{u.uses}</td>
                  <td className="py-2 pr-3 tabular-nums">{u.videos}</td>
                  <td className="py-2 text-xs">
                    {u.last_at
                      ? new Date(u.last_at).toLocaleString("fr-CA", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && uses === 0 && (
        <p className="mt-3 text-xs text-zinc-600" data-testid="legacy-stream-token-empty">
          Aucun usage enregistré sur les 30 derniers jours. Le journal est alimenté à
          chaque lecture qui passe encore par l&apos;ancien jeton, donc ce zéro est une
          preuve, pas une absence de mesure.
        </p>
      )}
    </section>
  );
};

export default LegacyStreamTokenCard;
